import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Newsletter = () => {
  const [form, setForm] = useState({ email: '', full_name: '', company_name: '', country: '', vat_number: '' });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, form);
      setStatus('success');
      setMessage('Successfully subscribed!');
      setForm({ email: '', full_name: '', company_name: '', country: '', vat_number: '' });
      setExpanded(false);
      setTimeout(() => setStatus('idle'), 4000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Subscription failed');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section className="relative py-20 overflow-hidden" data-testid="newsletter-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0096D6] to-[#007bb0]" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
            Start Receiving Wholesale Offers
          </h2>
          <p className="font-manrope text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Register your details and receive wholesale hardware offers directly to your inbox.
          </p>

          <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(p => ({...p, email: e.target.value}))}
                placeholder="Your business email"
                className="flex-1 px-5 py-3.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:border-white/40 font-manrope"
                required
                data-testid="newsletter-email-input"
              />
              {!expanded && (
                <button type="button" onClick={() => setExpanded(true)} className="px-6 py-3.5 rounded-full bg-white text-[#0096D6] font-semibold font-manrope hover:bg-opacity-90 transition-colors">
                  Subscribe
                </button>
              )}
            </div>

            {expanded && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-3 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={form.full_name} onChange={(e) => setForm(p => ({...p, full_name: e.target.value}))} placeholder="Full name" className="px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/60 font-manrope text-sm" />
                  <input type="text" value={form.company_name} onChange={(e) => setForm(p => ({...p, company_name: e.target.value}))} placeholder="Company name" className="px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/60 font-manrope text-sm" />
                  <input type="text" value={form.country} onChange={(e) => setForm(p => ({...p, country: e.target.value}))} placeholder="Country" className="px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/60 font-manrope text-sm" />
                  <input type="text" value={form.vat_number} onChange={(e) => setForm(p => ({...p, vat_number: e.target.value}))} placeholder="VAT number (if applicable)" className="px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/60 font-manrope text-sm" />
                </div>
                <p className="font-manrope text-white/50 text-xs">By subscribing you consent to receiving wholesale product offers from Real Tech Services Ltd.</p>
                <motion.button
                  type="submit"
                  disabled={status === 'loading'}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-white text-[#0096D6] font-semibold font-manrope flex items-center justify-center gap-2 hover:bg-opacity-90 disabled:opacity-70 mx-auto"
                  data-testid="newsletter-submit-button"
                >
                  {status === 'loading' ? 'Subscribing...' : status === 'success' ? <><CheckCircle className="w-5 h-5" /> Subscribed!</> : status === 'error' ? <><AlertCircle className="w-5 h-5" /> Try Again</> : <><Send className="w-5 h-5" /> Subscribe Now</>}
                </motion.button>
              </motion.div>
            )}

            {message && (
              <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`mt-4 font-manrope ${status === 'success' ? 'text-white' : 'text-red-200'}`} data-testid="newsletter-message">
                {message}
              </motion.p>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};
